import { useSearchParams } from 'react-router-dom';
import { getFilterConfig } from './config';
import {
	FILTER_PREFIX,
	OP_SUFFIX,
	SORT_DIRECTION_KEY,
	SORT_FIELD_KEY,
	VALUE_SUFFIX,
} from 'src/utils/constants';
import { useCallback } from 'react';

const setFilter = (searchParams, setSearchParams, code, op, value) => {
	const params = new URLSearchParams(searchParams);

	params.set(`filter.${code}.op`, op);
	params.set(
		`filter.${code}.value`,
		Array.isArray(value) ? value.join(',') : value,
	);

	if (code === 'status') {
		params.delete('filter.postponedUntil.op');
		params.delete('filter.postponedUntil.value');
	}

	params.set('page', 1);

	setSearchParams(params);
};

const transformFilterValue = (data, code, op) => {
	const config = getFilterConfig(code);

	if (!config) throw new Error(`Undefined code, failed transforming`);

	return config.transform(data, op);
};

export const parseFilterValue = (code, val, op) => {
	const config = getFilterConfig(code);

	return config?.parse(val, op);
};

export const parseFilters = (searchParams) => {
	const filters = {};

	for (const [key, val] of searchParams.entries()) {
		const parts = key?.split('.');

		if (parts.length !== 3 || parts[0] !== FILTER_PREFIX) continue;

		const [, code, prop] = parts;

		if (prop !== VALUE_SUFFIX) continue;

		const op = searchParams.get(`${FILTER_PREFIX}.${code}.${OP_SUFFIX}`);

		if (!op) continue;

		const parsedValue = parseFilterValue(code, val, op);

		if (parsedValue !== undefined) {
			filters[code] = { [op]: parsedValue };
		}
	}

	return filters;
};

const removeFilter = (searchParams, setSearchParams, code) => {
	const params = new URLSearchParams(searchParams);

	params.delete(`filter.${code}.op`);
	params.delete(`filter.${code}.value`);

	if (code === 'status') {
		params.delete('filter.postponedUntil.op');
		params.delete('filter.postponedUntil.value');
	}

	if (params.get(SORT_FIELD_KEY) === code) {
		params.delete(SORT_FIELD_KEY);
		params.delete(SORT_DIRECTION_KEY);
	}

	params.set(`page`, 1);

	setSearchParams(params);
};

export const useFilters = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const getFilter = useCallback(
		(code) => {
			const filters = parseFilters(searchParams);

			return filters[code];
		},
		[searchParams],
	);

	const getAllFilters = useCallback(() => {
		return parseFilters(searchParams);
	}, [searchParams]);

	return {
		setFilters: (...args) =>
			setFilter(searchParams, setSearchParams, ...args),
		transformFilterValue,
		parseFilters: () => parseFilters(searchParams),
		removeFilter: (code) =>
			removeFilter(searchParams, setSearchParams, code),
		getFilter,
		getAllFilters,
	};
};
